import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  Upload,
  User,
  Building2,
  Mail,
  Phone,
  DollarSign,
  Clock,
  Shield,
  Database,
  RefreshCw
} from 'lucide-react';
import { formatCurrency } from '../../services/api';

const BidderReview = () => {
  const navigate = useNavigate();
  const { subscriptionId } = useParams();
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [bidders, setBidders] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);

  // Mock data
  useEffect(() => {
    const mockDetails = {
      id: subscriptionId,
      bondName: 'Malaysia Infrastructure Bond 2024',
      isin: 'MY0123456789',
      issuer: 'Malaysia Infrastructure Corp',
      subscriptionStartDate: '2024-12-01T09:00:00Z',
      subscriptionEndDate: '2024-12-15T17:00:00Z',
      status: 'Active',
      totalNominalValue: 500000000,
      minSubscription: 100000,
      bondType: 'Conventional'
    };
    setSubscriptionDetails(mockDetails);

    const mockBidders = [
      {
        id: 1,
        bidderName: 'Ahmad Bin Abdullah',
        bidderType: 'Individual',
        kycStatus: 'Verified',
        walletAddress: '0x1234567890abcdef1234567890abcdef12345678',
        bidAmount: 250000,
        bidPrice: 100.5,
        contactEmail: 'ahmad.abdullah@email.com',
        contactPhone: '+60123456789',
        additionalNotes: 'High net worth individual, existing client',
        dateAdded: '2024-12-10T10:30:00Z'
      },
      {
        id: 2,
        bidderName: 'Malaysian Pension Fund Berhad',
        bidderType: 'Institutional Investor',
        kycStatus: 'Verified',
        walletAddress: '0xabcdef1234567890abcdef1234567890abcdef12',
        bidAmount: 5000000,
        bidPrice: 99.8,
        contactEmail: 'investments@mpfb.com.my',
        contactPhone: '+603-21234567',
        additionalNotes: 'Large institutional investor, long-term holder',
        dateAdded: '2024-12-09T14:15:00Z'
      },
      {
        id: 3,
        bidderName: 'Tech Investment Holdings',
        bidderType: 'Institutional Investor',
        kycStatus: 'Pending',
        walletAddress: '0x9876543210fedcba9876543210fedcba98765432',
        bidAmount: 1500000,
        bidPrice: 101.2,
        contactEmail: 'investments@tech-holdings.com',
        contactPhone: '+603-87654321',
        additionalNotes: 'Business documentation under review',
        dateAdded: '2024-12-11T09:15:00Z'
      }
    ];
    setBidders(mockBidders);
  }, [subscriptionId]);

  const verifiedBidders = bidders.filter(bidder => bidder.kycStatus === 'Verified');
  const pendingBidders = bidders.filter(bidder => bidder.kycStatus !== 'Verified');

  const totalVerifiedAmount = verifiedBidders.reduce((sum, bidder) => sum + bidder.bidAmount, 0);
  const totalPendingAmount = pendingBidders.reduce((sum, bidder) => sum + bidder.bidAmount, 0);

  const getKycStatusBadge = (status) => {
    const statusConfig = {
      'Verified': { color: 'green', icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800' },
      'Pending': { color: 'yellow', icon: Clock, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      'Rejected': { color: 'red', icon: AlertTriangle, bgColor: 'bg-red-100', textColor: 'text-red-800' }
    };
    
    const config = statusConfig[status] || statusConfig['Pending'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const handleSubmitToBlockchain = async () => {
    if (verifiedBidders.length === 0) {
      alert('No verified bidders to submit. Please ensure at least one bidder has verified status.');
      return;
    }

    setIsSubmitting(true);
    setSubmissionStatus('submitting');

    try {
      // Simulate blockchain submission
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Simulate random success/failure
      const success = Math.random() > 0.2; // 80% success rate
      
      if (success) {
        setSubmissionStatus('success');
        setTimeout(() => {
          navigate('/facility-agent/subscription', { 
            state: { 
              message: `Successfully submitted ${verifiedBidders.length} verified bidders to blockchain for ${subscriptionDetails.bondName}` 
            }
          });
        }, 2000);
      } else {
        throw new Error('Blockchain network congestion detected. Please try again later.');
      }
    } catch (error) {
      setSubmissionStatus('error');
      setIsSubmitting(false);
      alert(`Submission failed: ${error.message}`);
    }
  };

  if (!subscriptionDetails) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Review & Confirm Bidder Information</h1>
              <p className="mt-2 text-sm text-gray-600">
                {subscriptionDetails.bondName} - ISIN: {subscriptionDetails.isin}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={verifiedBidders.length === 0 || isSubmitting}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Submit to Blockchain
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Verified Bidders</p>
                <p className="text-2xl font-bold text-gray-900">{verifiedBidders.length}</p>
                <p className="text-sm text-gray-500">{formatCurrency(totalVerifiedAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-yellow-100">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Verification</p>
                <p className="text-2xl font-bold text-gray-900">{pendingBidders.length}</p>
                <p className="text-sm text-gray-500">{formatCurrency(totalPendingAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-blue-100">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Bid Amount</p>
                <p className="text-2xl font-bold text-gray-900">{bidders.length}</p>
                <p className="text-sm text-gray-500">{formatCurrency(totalVerifiedAmount + totalPendingAmount)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Submission Status */}
        {submissionStatus && (
          <div className="mb-6">
            {submissionStatus === 'submitting' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <RefreshCw className="h-5 w-5 text-blue-600 animate-spin mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-800">Submitting to Blockchain</h3>
                    <p className="text-sm text-blue-600 mt-1">
                      Processing {verifiedBidders.length} verified bidders. Please wait...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {submissionStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800">Successfully Submitted</h3>
                    <p className="text-sm text-green-600 mt-1">
                      All verified bidders have been recorded on-chain. Redirecting to dashboard...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {submissionStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Submission Failed</h3>
                    <p className="text-sm text-red-600 mt-1">
                      Please check the error message and try again.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Warning for Pending Bidders */}
        {pendingBidders.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Pending Verification Notice</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  {pendingBidders.length} bidder(s) have pending verification and will not be included in the blockchain submission. 
                  Only verified bidders can proceed to on-chain allocation.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Verified Bidders Section */}
        {verifiedBidders.length > 0 && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Shield className="h-5 w-5 text-green-500 mr-2" />
                  Verified Bidders ({verifiedBidders.length})
                </h3>
                <div className="text-sm text-gray-500">
                  Ready for blockchain submission
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bidder Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Wallet Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bid Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Information
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {verifiedBidders.map((bidder) => (
                      <tr key={bidder.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {bidder.bidderType === 'Individual' ? (
                              <User className="h-5 w-5 text-gray-400 mr-3" />
                            ) : (
                              <Building2 className="h-5 w-5 text-gray-400 mr-3" />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">{bidder.bidderName}</div>
                              <div className="text-xs text-gray-500">{bidder.bidderType}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs font-mono text-gray-600 bg-gray-100 rounded px-2 py-1">
                            {bidder.walletAddress}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(bidder.bidAmount)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Price: {bidder.bidPrice.toFixed(4)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-600">
                            <div className="flex items-center mb-1">
                              <Mail className="h-3 w-3 mr-1" />
                              {bidder.contactEmail}
                            </div>
                            {bidder.contactPhone && (
                              <div className="flex items-center">
                                <Phone className="h-3 w-3 mr-1" />
                                {bidder.contactPhone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getKycStatusBadge(bidder.kycStatus)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Pending Bidders Section */}
        {pendingBidders.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Clock className="h-5 w-5 text-yellow-500 mr-2" />
                  Pending Verification ({pendingBidders.length})
                </h3>
                <div className="text-sm text-yellow-600">
                  Will not be included in submission
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bidder Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bid Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pendingBidders.map((bidder) => (
                      <tr key={bidder.id} className="hover:bg-gray-50 opacity-75">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {bidder.bidderType === 'Individual' ? (
                              <User className="h-5 w-5 text-gray-400 mr-3" />
                            ) : (
                              <Building2 className="h-5 w-5 text-gray-400 mr-3" />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-600">{bidder.bidderName}</div>
                              <div className="text-xs text-gray-500">{bidder.bidderType}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            {formatCurrency(bidder.bidAmount)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Price: {bidder.bidPrice.toFixed(4)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getKycStatusBadge(bidder.kycStatus)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-500">
                            {bidder.additionalNotes || 'No additional notes'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <Database className="mx-auto h-12 w-12 text-primary-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Confirm Blockchain Submission
              </h3>
              <div className="text-sm text-gray-500 mb-4">
                <p>You are about to submit <strong>{verifiedBidders.length} verified bidders</strong> to the blockchain for:</p>
                <p className="font-medium text-gray-700 mt-2">{subscriptionDetails.bondName}</p>
                <p className="text-xs text-gray-400 mt-1">ISIN: {subscriptionDetails.isin}</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-yellow-700">
                  <strong>Warning:</strong> This action cannot be undone. Once submitted, the bidder information will be permanently recorded on the blockchain.
                </p>
              </div>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    handleSubmitToBlockchain();
                  }}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  Confirm Submission
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BidderReview; 